/**
 * PortfoliYo Lab — the strategy authoring API client (SDO-1).
 *
 * Separate from `lib/api.ts` for the same reason `labAuthApi.ts` is: that module is Trade's client,
 * and its `addSymphony`/`validateSymphonyDsl`/`deleteSymphony` post multipart forms to Trade's
 * routes with Trade's response shapes. Importing them here would put Trade API calls in the Lab
 * browser, which is the one thing SDO-1's acceptance criteria forbid outright — a shared React
 * component is fine, a shared Trade API call is not.
 *
 * Same-origin `/api`, always: the Lab process serves these routes, which is what makes authoring
 * work with Trade stopped.
 */

import type { LogicNode } from '../components/LogicTree';

const API_BASE = '/api';

/** How a strategy's definition is written. `api` is deliberately absent — see decision LAR-D1. */
export type LabSourceMode = 'dsl' | 'portfolio';

/**
 * One sleeve of a portfolio, and whether it could be found (LAR-1).
 *
 * The five statuses come from the shared resolver unmapped, because each has a different fix:
 * `missing` means upload that strategy, `ambiguous` means two of yours share a name, and
 * `nested_portfolio` means the reference has to change. "Unresolved" would cover all three and help
 * with none.
 */
export interface PortfolioDependency {
  key: string;
  name: string | null;
  root_id: string | null;
  ref: string | null;
  status: 'resolved' | 'missing' | 'ambiguous' | 'nested_portfolio' | 'invalid_code';
  resolved_id: number | null;
  resolved_name: string | null;
  ambiguous_matches: number | null;
  weight_when_true: number | null;
  weight_else: number | null;
}

/**
 * The portfolio validator's answer.
 *
 * `structurally_valid` and `all_deps_resolved` are separate on purpose: a malformed document and a
 * missing sleeve are different problems, and the form headlines them differently because the author
 * does different things about them.
 */
export interface PortfolioValidation {
  valid: boolean;
  structurally_valid: boolean;
  name: string | null;
  description: string | null;
  portfolio_id: string | null;
  errors: string[];
  dependencies: PortfolioDependency[];
  all_deps_resolved: boolean;
}

/**
 * One sleeve of a portfolio as its DOCUMENT declares it (Visualizer).
 *
 * Deliberately not `PortfolioDependency`: that type carries a resolution — `status`, `resolved_id`,
 * `ambiguous_matches` — because its route looked the sleeve up in the author's strategies. The
 * Visualizer looks nothing up, so it has nothing to report but what the document says, and a type
 * with five null resolution fields would imply a lookup happened and came back empty.
 */
export interface DeclaredSleeve {
  key: string;
  name: string | null;
  ref: string | null;
  root_id: string | null;
}

/** A portfolio's named, reusable sub-allocation, drawn as its own tree. */
export interface CompositeTree {
  key: string;
  name: string | null;
  tree: LogicNode | null;
}

/**
 * What a definition turned out to be, and what it says (Visualizer).
 *
 * `kind` and `valid` are separate, the way `structurally_valid` and `all_deps_resolved` are on
 * `PortfolioValidation` and for the same reason: "this is a portfolio, and here is what is wrong
 * with it" sends a reader somewhere useful, and "could not parse" does not.
 */
export interface DefinitionTree {
  kind: 'strategy' | 'portfolio' | 'unknown';
  valid: boolean;
  name: string | null;
  description: string | null;
  tree: LogicNode | null;
  composites: CompositeTree[];
  sleeves: DeclaredSleeve[];
  /** Tickers (a strategy) or sleeve/composite keys (a portfolio) — what `highlight` may name. */
  candidates: string[];
  node_count: number;
  /** The definition was larger than one drawing; what came back is the first `node_count` nodes. */
  truncated: boolean;
  error: string | null;
}

export interface LabStrategy {
  id: number;
  symphony_id: string;
  name: string | null;
  description: string | null;
  /**
   * The definition — on the responses of the routes that just wrote it. **Null from `list` and
   * from `get`** (#418): opening a strategy reads what it is, not the 30–60 MB it holds. The text
   * is `definition(id)`, fetched only when the inline editor opens on one small enough to edit.
   */
  composer_code: string | null;
  /** How long the definition is, in characters, and how it starts — computed by the server. */
  definition_chars: number;
  definition_excerpt: string;
  source_mode: string;
  validation_status: string | null;
  /** Trade's, displayed only — no call in this module writes it (decision SDO-D4). */
  allocation_percent: number;
  is_active: boolean;
  /** LAR-1 — null on a DSL strategy. */
  strategy_type: string | null;
  dependencies: PortfolioDependency[] | null;
  last_validated_at: string | null;
  /** A sleeve changed under a portfolio that was valid when saved. Fixed by revalidating. */
  needs_revalidation: boolean;
}

export interface LabStrategyVersion {
  id: number;
  version_hash: string;
  is_current: boolean;
  created_at: string | null;
  /** SDO-2. Optional because pre-SDO-2 rows the backfill has not reached carry neither. */
  version_number: number | null;
  definition_hash: string | null;
  /** The validation belonging to THIS definition, not to the strategy (decision SDO-D2). */
  validation_status: string | null;
  is_shortlisted: boolean;
}

/**
 * Why an export refusal was a refusal (SDO-5).
 *
 * The reason travels as data rather than being matched out of a message, because the UI has to tell
 * these apart: `not_validated` is fixed by validating, `validated_then_edited` by re-saving and
 * re-shortlisting, and `superseded_portfolio` by choosing a different version. Decision SDO-D2 asks
 * for exactly the first distinction by name.
 */
export interface ExportRefusal {
  reason: string;
  message: string;
}

export interface DslValidation {
  valid: boolean;
  /**
   * The identity this definition WOULD be stored under — its own `id` when it carries one, and
   * Engine's `dsl-` slug of the name when it does not.
   *
   * Reported before anything is created because it is not cosmetic: a portfolio resolves a sleeve
   * by matching `root_id` against exactly this, so an author who cannot see it cannot tell whether
   * the sleeve they are about to save is the one their portfolio references.
   */
  symphony_id: string | null;
  name: string | null;
  description: string | null;
  error: string | null;
}

export class LabStrategyError extends Error {
  readonly status: number;
  /** Set when the server named the refusal (SDO-5 export gates). */
  readonly reason: string | null;

  // Assigned in the body rather than as a constructor parameter property: this project builds with
  // `erasableSyntaxOnly`, which rejects the shorthand.
  constructor(message: string, status: number, reason: string | null = null) {
    super(message);
    this.name = 'LabStrategyError';
    this.status = status;
    this.reason = reason;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw await refusal(response);
  }
  return response.json() as Promise<T>;
}

/** The server's error as an exception, carrying the reason when it named one. */
async function refusal(response: Response): Promise<LabStrategyError> {
  let detail = `Request failed (${response.status})`;
  let reason: string | null = null;
  try {
    const payload = await response.json();
    if (typeof payload?.detail === 'string') detail = payload.detail;
    // The export gates answer `{detail: {reason, message}}` — an object, so the UI can act on which
    // refusal it was rather than on prose written to be read by a person.
    if (payload?.detail && typeof payload.detail === 'object') {
      detail = payload.detail.message ?? detail;
      reason = payload.detail.reason ?? null;
    }
  } catch {
    // A non-JSON error body is not worth failing differently over.
  }
  return new LabStrategyError(detail, response.status, reason);
}

export const labStrategyApi = {
  list: () => request<{ symphonies: LabStrategy[] }>('/symphonies'),

  /** The strategy described — everything but the definition, plus its size and an excerpt. */
  get: (id: number) => request<LabStrategy>(`/symphonies/${id}`),

  /**
   * The definition itself, as text (#418). Not through `request`: the server sends
   * `text/plain`, not JSON, because escaping a 60 MB export as a JSON string costs both ends a
   * pass over every byte for nothing.
   */
  definition: async (id: number): Promise<string> => {
    const response = await fetch(`${API_BASE}/symphonies/${id}/definition`, {
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw await refusal(response);
    }
    return response.text();
  },

  /**
   * Draw a definition without saving it, or resolving it, or requiring that it could be saved.
   *
   * Distinct from `validate` in what it is FOR, not just in what it returns: that one answers
   * "would this be accepted" for a caller about to create a row, and this one answers "what does
   * this say" for text that may never become a strategy at all. It accepts a Composer definition
   * (CLJ or JSON) and a `vrk-portfolio/v1` document without being told which, because the server
   * can tell and asking would be a question with a checkable answer.
   *
   * `highlight` is a ticker, or a sleeve/composite key, painted green wherever it is allocated.
   */
  definitionTree: (definition: string, highlight?: string) =>
    request<DefinitionTree>('/definition-tree', {
      method: 'POST',
      body: JSON.stringify({ definition, highlight: highlight || null }),
    }),

  /** Parses without saving. Answers 200 with `valid: false` for bad DSL — that is authoring, not an error. */
  validate: (composerCode: string) =>
    request<DslValidation>('/symphonies/validate', {
      method: 'POST',
      body: JSON.stringify({ composer_code: composerCode }),
    }),

  create: (body: { composer_code: string; symphony_id?: string; name?: string; description?: string }) =>
    request<LabStrategy>('/symphonies', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Validate a portfolio document without saving (LAR-1).
   *
   * Answers 200 with `valid: false` for a bad document, like DSL validation does, because this is
   * debounced against a textarea somebody is typing into.
   */
  validatePortfolio: (portfolioJson: string) =>
    request<PortfolioValidation>('/symphonies/validate-portfolio', {
      method: 'POST',
      body: JSON.stringify({ portfolio_json: portfolioJson }),
    }),

  /**
   * Create a sleeve-portfolio strategy (LAR-1).
   *
   * `portfolio_json` is text, not an object: the bytes the author supplied are what gets stored and
   * hashed, and round-tripping them through `JSON.parse`/`stringify` here would send the server
   * something the author never saw.
   *
   * A separate call from `create` rather than a mode flag, for the same reason the routes are
   * separate: they share a name and a description and nothing else.
   */
  createPortfolio: (body: { portfolio_json: string; name?: string; description?: string }) =>
    request<LabStrategy>('/symphonies/portfolio', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Re-check a saved portfolio's sleeves against the strategies that exist now (LAR-1).
   *
   * A portfolio's sleeves are separate strategies with their own histories, so editing one changes
   * what the portfolio resolves to without touching the portfolio's row.
   */
  revalidateDependencies: (id: number) =>
    request<PortfolioValidation>(`/symphonies/${id}/revalidate-dependencies`, { method: 'POST' }),

  /**
   * Edit. A DSL change mints a new version server-side.
   *
   * The body type deliberately cannot carry `allocation_percent`: allocation is Trade's deployment
   * configuration, and the type is where that stops being an intention and becomes unrepresentable.
   */
  update: (id: number, body: { composer_code?: string; name?: string; description?: string }) =>
    request<LabStrategy>(`/symphonies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  /** Deactivates. Research results reference strategies by id with no constraint behind them. */
  deactivate: (id: number) => request<LabStrategy>(`/symphonies/${id}`, { method: 'DELETE' }),

  versions: (id: number) => request<LabStrategyVersion[]>(`/symphonies/${id}/versions`),

  /**
   * Mark an exact version as a deployment candidate — or unmark it (SDO-2).
   *
   * On the version, never on the strategy: a flag on the mutable row would go on saying
   * "shortlisted" while the definition underneath it changed.
   */
  shortlist: (strategyId: number, versionId: number, shortlisted: boolean) =>
    request<LabStrategyVersion>(`/symphonies/${strategyId}/versions/${versionId}/shortlist`, {
      method: 'POST',
      body: JSON.stringify({ shortlisted }),
    }),

  /**
   * Download an exact version as a deployment package (SDO-5).
   *
   * Returns the file rather than saving it: the caller hands it to the browser, and a human carries
   * it to Trade. There is deliberately no call anywhere in this module that reaches Trade — the
   * file IS the transport, which is what keeps a research outage from stopping trading.
   */
  exportVersion: async (
    strategyId: number,
    versionId: number,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(
      `${API_BASE}/symphonies/${strategyId}/versions/${versionId}/export`,
      { credentials: 'same-origin' },
    );
    if (!response.ok) {
      throw await refusal(response);
    }
    // The server names the file; deriving one here would be a second naming rule to keep in step.
    const disposition = response.headers.get('content-disposition') ?? '';
    const matched = /filename="([^"]+)"/.exec(disposition);
    return {
      blob: await response.blob(),
      filename: matched?.[1] ?? `strategy-${strategyId}-v${versionId}.deployment.json`,
    };
  },
};
